import { FileText, Import, Settings, User, Users } from "lucide-react";
import { CanAccess, useCanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";
import { NotificationsMenu } from "../notifications/NotificationsMenu";
import { ChangelogPage } from "../misc/ChangelogPage";
import { orderFor, profileFor } from "./navigationOrder";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();

  const dashboard = useCanAccess({ resource: "dashboard", action: "list" });
  const contacts = useCanAccess({ resource: "contacts", action: "list" });
  const companies = useCanAccess({ resource: "companies", action: "list" });
  const deals = useCanAccess({ resource: "deals", action: "list" });
  const projects = useCanAccess({ resource: "projects", action: "list" });
  const tickets = useCanAccess({ resource: "tickets", action: "list" });
  const tasks = useCanAccess({ resource: "tasks", action: "list" });
  const users = useCanAccess({ resource: "sales", action: "list" });
  const assign = useCanAccess({ resource: "projects", action: "assign" });
  const leadsCreate = useCanAccess({ resource: "deals", action: "create" });

  const profile = profileFor((permission) => {
    switch (permission) {
      case "users.list":
        return users.canAccess === true;
      case "projects.assign":
        return assign.canAccess === true;
      case "leads.create":
        return leadsCreate.canAccess === true;
      case "tasks.list":
        return tasks.canAccess === true;
      case "contacts.list":
        return contacts.canAccess === true;
      case "projects.list":
        return projects.canAccess === true;
      default:
        return false;
    }
  });

  const tabs = orderFor(
    [
      {
        href: "/",
        label: translate("ra.page.dashboard"),
        visible: dashboard.canAccess === true,
      },
      {
        href: "/deals",
        label: translate("resources.deals.name", { smart_count: 2 }),
        visible: deals.canAccess === true,
      },
      {
        href: "/contacts",
        label: translate("resources.contacts.name", { smart_count: 2 }),
        visible: contacts.canAccess === true,
      },
      {
        href: "/companies",
        label: translate("resources.companies.name", { smart_count: 2 }),
        visible: companies.canAccess === true,
      },
      {
        href: "/projects",
        label: translate("resources.projects.name", {
          smart_count: 2,
          _: "Projects",
        }),
        visible: projects.canAccess === true,
      },
      {
        href: "/tickets",
        label: translate("resources.tickets.name", {
          smart_count: 2,
          _: "Tickets",
        }),
        visible: tickets.canAccess === true,
      },
      {
        href: "/tasks",
        label: translate("resources.tasks.name", { smart_count: 2 }),
        visible: tasks.canAccess === true,
      },
    ].filter((tab) => tab.visible),
    profile,
  );

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/projects/*", location.pathname)) {
    currentPath = "/projects";
  } else if (matchPath("/tickets/*", location.pathname)) {
    currentPath = "/tickets";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else {
    currentPath = false;
  }

  return (
    <>
      <nav className="grow">
        <header className="bg-secondary">
          <div className="px-4">
            <div className="flex justify-between items-center flex-1">
              <Link
                to="/"
                className="flex items-center gap-2 text-secondary-foreground no-underline"
              >
                <img
                  className="[.light_&]:hidden h-6"
                  src={darkModeLogo}
                  alt={title}
                />
                <img
                  className="[.dark_&]:hidden h-6"
                  src={lightModeLogo}
                  alt={title}
                />
                <h1 className="text-xl font-semibold">{title}</h1>
              </Link>
              <div>
                {/* Each tab is gated on the same permission that guards the
                    page behind it, so a role never sees a link that would
                    bounce it to /access-denied, and the order comes from the
                    viewer's profile -- the same arrangement the mobile bar
                    uses. See navigationOrder. */}
                <nav className="flex">
                  {tabs.map((tab) => (
                    <NavigationTab
                      key={tab.href}
                      label={tab.label}
                      to={tab.href}
                      isActive={currentPath === tab.href}
                    />
                  ))}
                </nav>
              </div>
              <div className="flex items-center">
                <NotificationsMenu />
                <ThemeModeToggle />
                <RefreshButton />
                <UserMenu>
                  <ProfileMenu />
                  <CanAccess resource="sales" action="list">
                    <UsersMenu />
                  </CanAccess>
                  <CanAccess resource="configuration" action="edit">
                    <SettingsMenu />
                  </CanAccess>
                  <ImportFromJsonMenuItem />
                  <ChangelogMenuItem />
                </UserMenu>
              </div>
            </div>
          </div>
        </header>
      </nav>
    </>
  );
};

const NavigationTab = ({
  label,
  to,
  isActive,
}: {
  label: string;
  to: string;
  isActive: boolean;
}) => (
  <Link
    to={to}
    className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
      isActive
        ? "text-secondary-foreground border-secondary-foreground"
        : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
    }`}
  >
    {label}
  </Link>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};
export default Header;
